import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError, createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from 'proto/gen/ts/shirt/v1/admin_pb';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatAddress } from '../../utils/orderTypes';
import { transport } from '../../utils/transport';
const adminClient = createClient(AdminService, transport);
const CARRIERS = [
    {
        value: 'usps',
        label: 'USPS'
    },
    {
        value: 'ups',
        label: 'UPS'
    },
    {
        value: 'fedex',
        label: 'FedEx'
    },
    {
        value: 'dhl',
        label: 'DHL'
    },
    {
        value: 'other',
        label: 'Other'
    }
];
function formatCents(n) {
    const v = typeof n === 'bigint' ? Number(n) : n;
    return `$${(v / 100).toFixed(2)}`;
}
function formatDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch  {
        return iso;
    }
}
export default function AdminOrderDetail() {
    const { orderId } = useParams();
    const queryClient = useQueryClient();
    const [carrier, setCarrier] = useState('usps');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [ownerNotes, setOwnerNotes] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            'adminOrder',
            orderId
        ],
        queryFn: ()=>adminClient.getOrder({
                orderId: orderId ?? ''
            }),
        enabled: !!orderId
    });
    const order = data?.order;
    useEffect(()=>{
        if (order) {
            setCarrier(order.trackingCarrier || 'usps');
            setTrackingNumber(order.trackingNumber || '');
            setOwnerNotes(order.ownerNotes || '');
        }
    }, [
        order
    ]);
    const updateMutation = useMutation({
        mutationFn: (input)=>adminClient.updateOrderFulfillment({
                orderId: orderId ?? '',
                status: input.status,
                trackingNumber: input.trackingNumber ?? '',
                trackingCarrier: input.trackingCarrier ?? '',
                ownerNotes: input.ownerNotes ?? ''
            }),
        onSuccess: (_res, variables)=>{
            setError('');
            setSuccess(`Marked ${variables.status}`);
            queryClient.invalidateQueries({
                queryKey: [
                    'adminOrder',
                    orderId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'adminOrders'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'adminDashboard'
                ]
            });
        },
        onError: (err)=>{
            setSuccess('');
            setError(err instanceof ConnectError ? err.message : 'Update failed');
        }
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-stone-500 p-8",
            children: "Loading order..."
        });
    }
    if (isError || !order) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-3xl mx-auto px-4 py-8",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-red-600 mb-4",
                    children: "Order not found."
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: "/admin/orders",
                    className: "text-sm underline",
                    children: "Back to orders"
                })
            ]
        });
    }
    let items = [];
    try {
        if (order.itemsJson) items = JSON.parse(order.itemsJson);
    } catch  {
    // ignore
    }
    const shipping = order.shippingAddress ? formatAddress(order.shippingAddress) : null;
    const canMarkPacked = order.fulfillmentStatus === 'pending';
    const canMarkShipped = order.fulfillmentStatus === 'pending' || order.fulfillmentStatus === 'packed';
    const canMarkDelivered = order.fulfillmentStatus === 'shipped';
    const canCancel = order.fulfillmentStatus !== 'cancelled' && order.fulfillmentStatus !== 'delivered';
    const saveNotes = ()=>updateMutation.mutate({
            status: order.fulfillmentStatus,
            trackingNumber: order.trackingNumber,
            trackingCarrier: order.trackingCarrier,
            ownerNotes
        });
    const markShipped = ()=>{
        if (!trackingNumber.trim()) {
            setError('Tracking number is required to mark shipped');
            return;
        }
        updateMutation.mutate({
            status: 'shipped',
            trackingNumber: trackingNumber.trim(),
            trackingCarrier: carrier,
            ownerNotes
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
        children: [
            /*#__PURE__*/ _jsx(Link, {
                to: "/admin/orders",
                className: "text-sm text-stone-500 hover:text-stone-900 inline-block mb-4",
                children: "← Back to orders"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3 mb-6",
                children: [
                    /*#__PURE__*/ _jsxs("h1", {
                        className: "font-heading text-2xl font-bold text-stone-900",
                        children: [
                            "Order ",
                            order.id.slice(0, 8)
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs uppercase tracking-wider bg-stone-100 text-stone-700 px-2 py-0.5",
                        children: order.fulfillmentStatus
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: "bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4",
                children: error
            }),
            success && /*#__PURE__*/ _jsx("div", {
                className: "bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm mb-4",
                children: success
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 lg:grid-cols-3 gap-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "lg:col-span-2 space-y-6",
                        children: [
                            /*#__PURE__*/ _jsxs("section", {
                                className: "bg-white border-2 border-stone-100 p-5",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3",
                                        children: "Customer"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-stone-900",
                                        children: order.email
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-xs text-stone-500 mt-1",
                                        children: [
                                            "Total: ",
                                            formatCents(order.totalCents)
                                        ]
                                    })
                                ]
                            }),
                            shipping && /*#__PURE__*/ _jsxs("section", {
                                className: "bg-white border-2 border-stone-100 p-5",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3",
                                        children: "Shipping to"
                                    }),
                                    shipping.name && /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-semibold text-stone-900",
                                        children: shipping.name
                                    }),
                                    shipping.lines.map((line)=>/*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-stone-700",
                                            children: line
                                        }, line))
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                className: "bg-white border-2 border-stone-100 p-5",
                                children: [
                                    /*#__PURE__*/ _jsxs("h2", {
                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3",
                                        children: [
                                            "Items (",
                                            items.length,
                                            ")"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "space-y-3",
                                        children: items.map((item)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-3",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "w-12 h-12 bg-stone-100 flex-shrink-0 overflow-hidden",
                                                        children: item.image_url && /*#__PURE__*/ _jsx("img", {
                                                            src: item.image_url,
                                                            alt: item.name,
                                                            className: "w-full h-full object-cover"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-sm text-stone-900",
                                                                children: item.name
                                                            }),
                                                            /*#__PURE__*/ _jsxs("p", {
                                                                className: "text-xs text-stone-500",
                                                                children: [
                                                                    "Qty: ",
                                                                    item.quantity,
                                                                    " · ",
                                                                    formatCents(item.price_cents),
                                                                    " ea"
                                                                ]
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-sm text-stone-900 font-semibold",
                                                        children: formatCents(item.price_cents * item.quantity)
                                                    })
                                                ]
                                            }, item.product_id))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                className: "bg-white border-2 border-stone-100 p-5",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3",
                                        children: "Timeline"
                                    }),
                                    /*#__PURE__*/ _jsxs("dl", {
                                        className: "grid grid-cols-2 gap-y-2 text-sm",
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                className: "text-stone-500",
                                                children: "Paid"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                className: "text-stone-900",
                                                children: formatDate(order.paidAt)
                                            }),
                                            /*#__PURE__*/ _jsx("dt", {
                                                className: "text-stone-500",
                                                children: "Packed"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                className: "text-stone-900",
                                                children: formatDate(order.packedAt)
                                            }),
                                            /*#__PURE__*/ _jsx("dt", {
                                                className: "text-stone-500",
                                                children: "Shipped"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                className: "text-stone-900",
                                                children: formatDate(order.shippedAt)
                                            }),
                                            /*#__PURE__*/ _jsx("dt", {
                                                className: "text-stone-500",
                                                children: "Delivered"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                className: "text-stone-900",
                                                children: formatDate(order.deliveredAt)
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("aside", {
                        className: "space-y-6",
                        children: [
                            /*#__PURE__*/ _jsxs("section", {
                                className: "bg-white border-2 border-stone-100 p-5",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3",
                                        children: "Actions"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2",
                                        children: [
                                            canMarkPacked && /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>updateMutation.mutate({
                                                        status: 'packed',
                                                        trackingNumber: order.trackingNumber,
                                                        trackingCarrier: order.trackingCarrier,
                                                        ownerNotes
                                                    }),
                                                disabled: updateMutation.isPending,
                                                className: "w-full py-3 bg-stone-900 text-white text-xs font-semibold uppercase tracking-wider hover:bg-stone-700 disabled:opacity-60",
                                                children: "Mark Packed"
                                            }),
                                            canMarkDelivered && /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>updateMutation.mutate({
                                                        status: 'delivered',
                                                        trackingNumber: order.trackingNumber,
                                                        trackingCarrier: order.trackingCarrier,
                                                        ownerNotes
                                                    }),
                                                disabled: updateMutation.isPending,
                                                className: "w-full py-3 bg-green-700 text-white text-xs font-semibold uppercase tracking-wider hover:bg-green-800 disabled:opacity-60",
                                                children: "Mark Delivered"
                                            }),
                                            canCancel && /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>{
                                                    if (window.confirm('Cancel this order?')) {
                                                        updateMutation.mutate({
                                                            status: 'cancelled',
                                                            trackingNumber: order.trackingNumber,
                                                            trackingCarrier: order.trackingCarrier,
                                                            ownerNotes
                                                        });
                                                    }
                                                },
                                                disabled: updateMutation.isPending,
                                                className: "w-full py-3 border-2 border-red-600 text-red-700 text-xs font-semibold uppercase tracking-wider hover:bg-red-50 disabled:opacity-60",
                                                children: "Cancel Order"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                className: "bg-white border-2 border-stone-100 p-5",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3",
                                        children: "Tracking"
                                    }),
                                    order.trackingNumber ? /*#__PURE__*/ _jsxs("div", {
                                        className: "text-sm space-y-1 mb-4",
                                        children: [
                                            /*#__PURE__*/ _jsxs("p", {
                                                className: "text-stone-900",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-stone-500",
                                                        children: "Carrier:"
                                                    }),
                                                    " ",
                                                    order.trackingCarrier
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-stone-900 font-mono break-all",
                                                children: order.trackingNumber
                                            }),
                                            order.trackingUrl && /*#__PURE__*/ _jsx("a", {
                                                href: order.trackingUrl,
                                                target: "_blank",
                                                rel: "noopener noreferrer",
                                                className: "text-xs underline text-stone-700",
                                                children: "Open tracking page"
                                            })
                                        ]
                                    }) : null,
                                    canMarkShipped && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                htmlFor: "carrier",
                                                className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                                children: "Carrier"
                                            }),
                                            /*#__PURE__*/ _jsx("select", {
                                                id: "carrier",
                                                value: carrier,
                                                onChange: (e)=>setCarrier(e.target.value),
                                                className: "w-full border-2 border-stone-200 px-3 py-2 text-sm mb-3 focus:border-stone-900 focus:outline-none",
                                                children: CARRIERS.map((c)=>/*#__PURE__*/ _jsx("option", {
                                                        value: c.value,
                                                        children: c.label
                                                    }, c.value))
                                            }),
                                            /*#__PURE__*/ _jsx("label", {
                                                htmlFor: "tracking",
                                                className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                                children: "Tracking number"
                                            }),
                                            /*#__PURE__*/ _jsx("input", {
                                                id: "tracking",
                                                type: "text",
                                                value: trackingNumber,
                                                onChange: (e)=>setTrackingNumber(e.target.value),
                                                className: "w-full border-2 border-stone-200 px-3 py-2 text-sm mb-3 font-mono focus:border-stone-900 focus:outline-none",
                                                placeholder: "9400 1234 5678..."
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: markShipped,
                                                disabled: updateMutation.isPending || !trackingNumber.trim(),
                                                className: "w-full py-3 bg-indigo-700 text-white text-xs font-semibold uppercase tracking-wider hover:bg-indigo-800 disabled:opacity-60",
                                                children: "Mark Shipped + Email Customer"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                className: "bg-white border-2 border-stone-100 p-5",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3",
                                        children: "Notes"
                                    }),
                                    /*#__PURE__*/ _jsx("textarea", {
                                        value: ownerNotes,
                                        onChange: (e)=>setOwnerNotes(e.target.value),
                                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm min-h-[80px] focus:border-stone-900 focus:outline-none",
                                        placeholder: "Private notes for yourself"
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: saveNotes,
                                        disabled: updateMutation.isPending,
                                        className: "mt-2 w-full py-2 border-2 border-stone-200 text-stone-700 text-xs font-semibold uppercase tracking-wider hover:border-stone-900 disabled:opacity-60",
                                        children: "Save Notes"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
